import { MainLayout } from '@/components/layout/MainLayout';
import { AllDocsFolders } from '@/components/documents/AllDocsFolders';
import { useState, useEffect } from 'react';
import { getSettings } from '@/lib/api/settings';

interface CompanyInfo {
  companyName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  website: string;
  licenseNumber: string;
}

interface DocumentForm {
  id: string;
  title: string;
  description: string;
  category: 'client' | 'employee';
  icon: string;
  order: number;
}

const documents: DocumentForm[] = [
  // Client Onboarding Forms (order 1-5 for logical sequence)
  {
    id: 'client-info',
    title: 'Client Information Form',
    description: 'Collect basic client details, contact information, and billing address',
    category: 'client',
    icon: 'person',
    order: 1,
  },
  {
    id: 'project-scope',
    title: 'Project Scope Agreement',
    description: 'Define project scope, deliverables, timeline, and client expectations',
    category: 'client',
    icon: 'assignment',
    order: 2,
  },
  {
    id: 'service-agreement',
    title: 'Service Agreement',
    description: 'Terms of service, payment terms, and liability disclaimers',
    category: 'client',
    icon: 'handshake',
    order: 3,
  },
  {
    id: 'change-order',
    title: 'Change Order Form',
    description: 'Document scope changes, cost adjustments, and timeline modifications',
    category: 'client',
    icon: 'edit_note',
    order: 4,
  },
  {
    id: 'completion-signoff',
    title: 'Project Completion Sign-Off',
    description: 'Final walkthrough checklist and client acceptance signature',
    category: 'client',
    icon: 'task_alt',
    order: 5,
  },
  // Employee Onboarding Forms (order 1-10 for logical sequence)
  {
    id: 'employee-info',
    title: 'Employee Information Form',
    description: 'Personal details, emergency contacts, and employment eligibility',
    category: 'employee',
    icon: 'badge',
    order: 1,
  },
  {
    id: 'td1-federal',
    title: 'TD1 Federal - Personal Tax Credits Return',
    description: 'Official CRA federal tax form TD1-26E (2026)',
    category: 'employee',
    icon: 'account_balance',
    order: 2,
  },
  {
    id: 'td1-provincial',
    title: 'TD1BC Provincial - Personal Tax Credits Return',
    description: 'Official CRA provincial tax form TD1BC-26E for British Columbia (2026)',
    category: 'employee',
    icon: 'location_city',
    order: 3,
  },
  {
    id: 'direct-deposit',
    title: 'Direct Deposit Authorization',
    description: 'Bank account information for payroll deposits',
    category: 'employee',
    icon: 'payments',
    order: 4,
  },
    {
    id: 'code-of-conduct',
    title: 'Code of Conduct Agreement',
    description: 'Professional conduct, workplace expectations, and behavioural standards',
    category: 'employee',
    icon: 'gavel',
    order: 7,
  },
      {
    id: 'biweekly-timesheet',
    title: 'Biweekly Timesheet',
    description: 'Track hours worked, job sites, and tasks for each pay period',
    category: 'employee',
    icon: 'schedule',
    order: 10,
  },
];

// PDF-based documents that should show the PDF directly instead of HTML form
const pdfDocuments: Record<string, string> = {
  'td1-federal': '/forms/td1-federal-26e.pdf',
  'td1-provincial': '/forms/td1bc-provincial-26e.pdf',
};

const handlePreview = (doc: DocumentForm, companyInfo?: CompanyInfo) => {
  // For PDF documents, open the PDF directly
  if (pdfDocuments[doc.id]) {
    window.open(pdfDocuments[doc.id], '_blank');
    return;
  }
  const formContent = generateFormHTML(doc, companyInfo);
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) return;
  previewWindow.document.write(formContent);
  previewWindow.document.close();
};

const handlePrint = (doc: DocumentForm, companyInfo?: CompanyInfo) => {
  // For PDF documents, open the PDF for printing
  if (pdfDocuments[doc.id]) {
    const printWindow = window.open(pdfDocuments[doc.id], '_blank');
    if (printWindow) {
      printWindow.onload = () => printWindow.print();
    }
    return;
  }
  const formContent = generateFormHTML(doc, companyInfo);
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(formContent);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};

const generateFormHTML = (doc: DocumentForm, companyInfo?: CompanyInfo): string => {
  const company = companyInfo || {
    companyName: 'Your Company',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: ''
  };
  const formFields: Record<string, string> = {
    'client-info': `
      <div class="form-section">
        <h2>Client Information</h2>
        <div class="form-row">
          <label>Company/Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Primary Contact:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Email Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Billing Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>City, Province/State, Postal Code:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Site Address (if different):</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Preferred Contact Method:</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Phone</span>
            <span>☐ Email</span>
            <span>☐ Text</span>
          </div>
        </div>
        <div class="form-row">
          <label>How did you hear about us?</label>
          <div class="input-line"></div>
        </div>
      </div>
    `,
    'project-scope': `
      <div class="form-section">
        <h2>Project Information</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Start Date:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Estimated Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Scope of Work</h2>
        <div class="form-row">
          <label>Detailed Description of Work:</label>
          <div class="textarea-box large"></div>
        </div>
        <div class="form-row">
          <label>Materials to be Used:</label>
          <div class="textarea-box"></div>
        </div>
        <div class="form-row">
          <label>Work NOT Included in Scope:</label>
          <div class="textarea-box"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Deliverables</h2>
        <p class="section-description">List specific items, structures, or results that will be completed as part of this project (e.g., "Install 200 sq ft hardwood flooring", "Frame and drywall basement bedroom", "Replace 5 windows with vinyl double-pane"):</p>
        <div class="checklist">
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
        </div>
      </div>
    `,
    'service-agreement': `
      <div class="form-section">
        <h2>Parties</h2>
        <div class="form-row">
          <label>Contractor:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Client:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Terms of Service</h2>
        <div class="terms-text">
          <p>1. <strong>Scope of Services:</strong> The Contractor agrees to provide construction and/or renovation services as outlined in the attached Project Scope Agreement.</p>
          <p>2. <strong>Payment Terms:</strong> The Client agrees to pay _____% deposit upon signing, with the balance due upon completion. Payment is due within 30 days of the invoice date. Interest of 2% per month may be charged on overdue accounts.</p>
          <p>3. <strong>Change Orders:</strong> Any changes to the original scope must be documented in writing and signed by both parties before additional work proceeds. Changes may affect the project timeline and total cost.</p>
          <p>4. <strong>Timeline:</strong> Work will commence on __________ with estimated completion by __________. The timeline may be extended due to weather, material availability, permit delays, or Client-requested changes.</p>
          <p>5. <strong>Permits and Inspections:</strong> The Contractor is responsible for obtaining required building permits and scheduling inspections, unless otherwise agreed in writing. Permit fees are ☐ included / ☐ not included in the contract price.</p>
          <p>6. <strong>Warranty:</strong> The Contractor provides a minimum 2-year warranty on workmanship from the date of completion. Manufacturer warranties apply to all materials and fixtures. This warranty does not cover damage caused by misuse, neglect, or normal wear and tear.</p>
          <p>7. <strong>Insurance:</strong> The Contractor maintains commercial general liability insurance and is registered with WorkSafeBC for workers' compensation coverage. Proof of insurance is available upon request.</p>
          <p>8. <strong>Cancellation:</strong> Either party may cancel this agreement with 14 days written notice. Upon cancellation, the Client is responsible for payment of all work completed, materials purchased or ordered, and any restocking fees. Deposits are non-refundable for custom or special-order materials.</p>
          <p>9. <strong>Builders Lien Notice:</strong> Pursuant to the Builders Lien Act of British Columbia, the Contractor and any subcontractors or material suppliers may have lien rights against the property if payment is not received.</p>
          <p>10. <strong>Dispute Resolution:</strong> The parties agree to attempt to resolve any disputes through good-faith negotiation. If unresolved, disputes shall be submitted to mediation before pursuing legal remedies. This agreement is governed by the laws of British Columbia.</p>
          <p>11. <strong>Entire Agreement:</strong> This agreement, together with any attached schedules and the Project Scope Agreement, constitutes the entire agreement between the parties. Any modifications must be in writing and signed by both parties.</p>
        </div>
      </div>
      <div class="form-section">
        <h2>Signatures</h2>
        <p class="section-description">By signing below, both parties acknowledge that they have read, understood, and agree to the terms of this Service Agreement.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'change-order': `
      <div class="form-section">
        <h2>Original Project Information</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Original Contract Date:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Change Order #:</label>
          <div class="input-line short"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Description of Change</h2>
        <div class="form-row">
          <label>Detailed Description:</label>
          <div class="textarea-box large"></div>
        </div>
        <div class="form-row">
          <label>Reason for Change:</label>
          <div class="textarea-box"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Cost Adjustment</h2>
        <div class="form-row inline">
          <label>Additional Materials Cost: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>Additional Labour Cost: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>Total Change Order Amount: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>New Contract Total: $</label>
          <div class="input-line short"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Timeline Impact</h2>
        <div class="form-row">
          <label>Days Added to Schedule:</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>New Estimated Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Authorization</h2>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature / Date</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature / Date</label>
          </div>
        </div>
      </div>
    `,
    'completion-signoff': `
      <div class="form-section">
        <h2>Project Details</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Work Completion Statement</h2>
        <div style="border: 2px solid #333; padding: 16px; margin-bottom: 16px; line-height: 2;">
          <p>I, the undersigned Client, acknowledge and confirm that all work agreed upon for the project:</p>
          <p style="margin-top: 12px;">
            <strong>Project/Job Name:</strong> ________________________________________
          </p>
          <p style="margin-top: 8px;">
            <strong>Project Start Date:</strong> ________________________________________
          </p>
          <p style="margin-top: 12px;">
            has been completed in accordance with the terms of the Service Agreement and Project Scope Agreement. 
            All deliverables specified in the original contract and any approved change orders have been fulfilled to my satisfaction.
          </p>
          <div style="display: flex; justify-content: flex-end; align-items: center; gap: 8px; margin-top: 16px;">
            <span>Client Initials:</span>
            <div style="border: 2px solid #333; width: 60px; height: 40px;"></div>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2 style="display: inline;">Omissions</h2>
        <span class="section-description" style="margin-left: 12px;">List any work from the original scope that was not completed or requires follow-up</span>
        <div style="margin-top: 12px;">
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Client Acceptance</h2>
        <p>I have inspected the completed work and accept it as satisfactory. All contracted work has been performed to my satisfaction.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'employee-info': `
      <div class="form-section">
        <h2>Personal Information</h2>
        <div class="form-row">
          <label>Full Legal Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Date of Birth:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label style="display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY, e.g. JohnSmith010190)</span></label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Social Insurance Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Home Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>City, Province, Postal Code:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Email Address:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Emergency Contact</h2>
        <div class="form-row">
          <label>Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Relationship:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Employment Eligibility</h2>
        <div class="form-row">
          <label>Are you legally eligible to work in Canada?</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Yes</span>
            <span>☐ No</span>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2>Certification</h2>
        <p>I certify that the information provided above is true and accurate to the best of my knowledge.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'td1-federal': `
      <div class="form-section">
        <h2>TD1 Federal - 2026 Personal Tax Credits Return</h2>
        <p class="note" style="margin-bottom: 16px; font-style: italic; color: #666;">
          This is the official CRA TD1 Federal form. Please print or download the PDF below and complete it as required.
        </p>
        <div style="text-align: center; margin: 24px 0;">
          <a href="/forms/td1-federal-26e.pdf" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #2C5282; color: white; text-decoration: none; border-radius: 8px; font-weight: bold;">
            <span style="font-size: 20px;">📄</span>
            Open TD1 Federal Form (PDF)
          </a>
        </div>
        <div style="border: 2px solid #2C5282; border-radius: 8px; overflow: hidden; margin-top: 16px;">
          <iframe 
            src="/forms/td1-federal-26e.pdf" 
            style="width: 100%; height: 800px; border: none;"
            title="TD1 Federal Form">
          </iframe>
        </div>
        <p style="margin-top: 16px; font-size: 10pt; color: #666; text-align: center;">
          Source: Canada Revenue Agency (CRA) - Form TD1-26E
        </p>
      </div>
    `,
    'td1-provincial': `
      <div class="form-section">
        <h2>TD1BC Provincial - 2026 British Columbia Personal Tax Credits Return</h2>
        <p class="note" style="margin-bottom: 16px; font-style: italic; color: #666;">
          This is the official CRA TD1BC Provincial form for British Columbia. Please print or download the PDF below and complete it as required.
        </p>
        <div style="text-align: center; margin: 24px 0;">
          <a href="/forms/td1bc-provincial-26e.pdf" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #2C5282; color: white; text-decoration: none; border-radius: 8px; font-weight: bold;">
            <span style="font-size: 20px;">📄</span>
            Open TD1BC Provincial Form (PDF)
          </a>
        </div>
        <div style="border: 2px solid #2C5282; border-radius: 8px; overflow: hidden; margin-top: 16px;">
          <iframe 
            src="/forms/td1bc-provincial-26e.pdf" 
            style="width: 100%; height: 800px; border: none;"
            title="TD1BC Provincial Form">
          </iframe>
        </div>
        <p style="margin-top: 16px; font-size: 10pt; color: #666; text-align: center;">
          Source: Canada Revenue Agency (CRA) - Form TD1BC-26E
        </p>
      </div>
    `,
    'direct-deposit': `
      <div class="form-section">
        <h2>Employee Information</h2>
        <div class="form-row">
          <label>Full Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label style="display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY, e.g. JohnSmith010190)</span></label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Bank Account Information</h2>
        <div class="form-row">
          <label>Financial Institution Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Branch Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Account Type:</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Chequing</span>
            <span>☐ Savings</span>
          </div>
        </div>
        <div class="form-row">
          <label>Institution Number (3 digits):</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>Transit/Branch Number (5 digits):</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>Account Number:</label>
          <div class="input-line"></div>
        </div>
        <p class="note" style="font-style: italic; color: #666; margin-top: 12px;">Please attach a void cheque or a direct deposit form from your financial institution confirming the account details above.</p>
      </div>
      <div class="form-section">
        <h2>Deposit Instructions</h2>
        <div class="form-row">
          <label>Deposit Amount:</label>
          <div style="margin-top: 8px;">
            <div style="margin-bottom: 8px;">☐ 100% of net pay</div>
            <div style="margin-bottom: 8px;">☐ Fixed amount: $__________</div>
            <div style="margin-bottom: 8px;">☐ Percentage: __________% of net pay</div>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2>Authorization</h2>
        <p>I hereby authorize my employer to deposit my pay directly into the bank account specified above. I understand that:</p>
        <ul style="margin-left: 24px; margin-top: 8px; margin-bottom: 12px;">
          <li>It may take 1-2 pay periods for direct deposit to take effect.</li>
          <li>I am responsible for ensuring the accuracy of the account information provided.</li>
          <li>I must notify my employer in writing of any changes to my banking information.</li>
          <li>This authorization remains in effect until I provide written notice of cancellation.</li>
        </ul>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
        'code-of-conduct': `
      <style>
        .coc-section { margin-bottom: 8px; }
        .coc-section h2 { font-size: 10pt; margin-bottom: 4px; }
        .coc-section p { font-size: 9pt; margin-bottom: 2px; line-height: 1.3; }
        .coc-list { margin-left: 16px; margin-top: 4px; }
        .coc-list p { margin-bottom: 1px; }
      </style>
      <div class="coc-section">
        <div class="form-row" style="margin-bottom: 6px;">
          <label style="font-size: 9pt;">Full Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row" style="margin-bottom: 6px;">
          <label style="font-size: 9pt; display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY)</span></label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="coc-section">
        <h2>A. Professional Conduct</h2>
        <p>As an employee, I agree to:</p>
        <div class="coc-list">
          <p><strong>A1.</strong> Arrive on time and ready to work at the designated start time</p>
          <p><strong>A2.</strong> Notify my supervisor as soon as possible if I will be late or absent</p>
          <p><strong>A3.</strong> Maintain a professional appearance appropriate for construction work</p>
          <p><strong>A4.</strong> Represent the company positively when interacting with clients, suppliers, and the public</p>
          <p><strong>A5.</strong> Follow all instructions from supervisors and site foremen</p>
          <p><strong>A6.</strong> Complete assigned tasks to the best of my ability and to company standards</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>B. Workplace Behaviour</h2>
        <p>I understand that the following behaviours are prohibited and may result in immediate termination:</p>
        <div class="coc-list">
          <p><strong>B1.</strong> Reporting to work under the influence of alcohol or illegal drugs</p>
          <p><strong>B2.</strong> Possession or use of alcohol or illegal drugs on company time or property</p>
          <p><strong>B3.</strong> Theft of company, client, or co-worker property</p>
          <p><strong>B4.</strong> Violence, threats, or intimidation toward any person</p>
          <p><strong>B5.</strong> Harassment or discrimination of any kind</p>
          <p><strong>B6.</strong> Wilful damage to property, tools, or equipment</p>
          <p><strong>B7.</strong> Falsifying timesheets, records, or other company documents</p>
          <p><strong>B8.</strong> Insubordination or refusal to follow reasonable instructions</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>C. Jobsite Expectations</h2>
        <p>While on any jobsite, I agree to:</p>
        <div class="coc-list">
          <p><strong>C1.</strong> Respect the client's property and belongings at all times</p>
          <p><strong>C2.</strong> Keep work areas clean and organized throughout the day</p>
          <p><strong>C3.</strong> Use designated areas for breaks and meals only</p>
          <p><strong>C4.</strong> Not use personal phones except during designated breaks</p>
          <p><strong>C5.</strong> Secure tools and materials at the end of each workday</p>
          <p><strong>C6.</strong> Report any damage, accidents, or incidents immediately to my supervisor</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>D. Tools and Equipment</h2>
        <p>I understand and agree that:</p>
        <div class="coc-list">
          <p><strong>D1.</strong> Company tools and equipment are for work purposes only</p>
          <p><strong>D2.</strong> I am responsible for the care and proper use of tools issued to me</p>
          <p><strong>D3.</strong> Lost, stolen, or damaged tools due to negligence may be deducted from my pay</p>
          <p><strong>D4.</strong> I will not lend company tools to others without authorization</p>
          <p><strong>D5.</strong> I will report any defective or damaged tools immediately</p>
          <p><strong>D6.</strong> All tools must be returned upon termination of employment</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>E. Confidentiality</h2>
        <p>I agree to keep confidential: client personal information and property details, company pricing, estimates, and business information, and co-worker personal information and compensation details.</p>
      </div>
      <div class="coc-section">
        <h2>F. Disciplinary Action</h2>
        <p>I understand that violations may result in: verbal warning, written warning, suspension without pay, or termination of employment. The company reserves the right to determine appropriate action based on severity. Serious violations may result in immediate termination.</p>
      </div>
      <div class="coc-section">
        <h2>Acknowledgment and Agreement</h2>
        <p style="margin-bottom: 8px;">By signing below, I acknowledge that I have read and understand this Code of Conduct Agreement. I agree to abide by these standards and understand that failure to do so may result in disciplinary action up to and including termination.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Date</label>
          </div>
        </div>
        <div class="signature-row" style="margin-top: 8px;">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Supervisor/Witness Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Date</label>
          </div>
        </div>
      </div>
    `,
            'biweekly-timesheet': `
      <div class="form-section" style="margin-bottom: 10px;">
        <div class="form-row" style="margin-bottom: 8px;">
          <label style="font-size: 9pt;">Employee Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row" style="margin-bottom: 8px;">
          <label style="display: flex; justify-content: space-between; width: 100%; font-size: 9pt;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY, e.g. JohnSmith010190)</span></label>
          <div class="input-line"></div>
        </div>
      </div>

      <div class="form-section" style="margin-bottom: 10px;">
        <h2 style="font-size: 11pt; margin-bottom: 6px;">Week 1</h2>
        <table class="timesheet-table" style="width: 100%; border-collapse: collapse; font-size: 8pt;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th style="border: 1px solid #333; padding: 3px; width: 70px;">Date</th>
              <th style="border: 1px solid #333; padding: 3px; width: 35px;">Day</th>
              <th style="border: 1px solid #333; padding: 3px;">Location</th>
              <th style="border: 1px solid #333; padding: 3px; width: 70px; white-space: nowrap;">Time of Start</th>
              <th style="border: 1px solid #333; padding: 3px; width: 65px; white-space: nowrap;">Time of End</th>
              <th style="border: 1px solid #333; padding: 3px; width: 75px; white-space: nowrap;">Time of Lunch</th>
              <th style="border: 1px solid #333; padding: 3px; width: 90px; white-space: nowrap;">Total Hrs Worked</th>
              <th style="border: 1px solid #333; padding: 3px;">Work Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Mon</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Tue</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Wed</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Thu</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Fri</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Sat</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Sun</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
          </tbody>
        </table>
      </div>

      <div class="form-section" style="margin-bottom: 10px;">
        <h2 style="font-size: 11pt; margin-bottom: 6px;">Week 2</h2>
        <table class="timesheet-table" style="width: 100%; border-collapse: collapse; font-size: 8pt;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th style="border: 1px solid #333; padding: 3px; width: 70px;">Date</th>
              <th style="border: 1px solid #333; padding: 3px; width: 35px;">Day</th>
              <th style="border: 1px solid #333; padding: 3px;">Location</th>
              <th style="border: 1px solid #333; padding: 3px; width: 70px; white-space: nowrap;">Time of Start</th>
              <th style="border: 1px solid #333; padding: 3px; width: 65px; white-space: nowrap;">Time of End</th>
              <th style="border: 1px solid #333; padding: 3px; width: 75px; white-space: nowrap;">Time of Lunch</th>
              <th style="border: 1px solid #333; padding: 3px; width: 90px; white-space: nowrap;">Total Hrs Worked</th>
              <th style="border: 1px solid #333; padding: 3px;">Work Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Mon</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Tue</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Wed</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Thu</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Fri</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Sat</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
            <tr><td style="border: 1px solid #333; padding: 4px; height: 18px;"></td><td style="border: 1px solid #333; padding: 4px;">Sun</td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td><td style="border: 1px solid #333; padding: 4px;"></td></tr>
          </tbody>
        </table>
      </div>

      <div class="form-section" style="margin-bottom: 0;">
        <p style="font-size: 9pt; margin-bottom: 6px;">I certify that the hours reported above are accurate and complete to the best of my knowledge.</p>
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
          <label style="font-size: 9pt; font-weight: bold;">Total Hours Worked:</label>
          <div style="border-bottom: 1px solid #333; width: 100px;"></div>
        </div>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Employee Signature</label>
          </div>
        </div>
      </div>
    `,
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${doc.title} - ${company.companyName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 12pt;
          line-height: 1.4;
          padding: 0.75in;
          max-width: 8.5in;
          margin: 0 auto;
        }
        .header {
          margin-bottom: 24px;
          padding-bottom: 8px;
          border-bottom: 2px solid #2C5282;
        }
        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .header h1 {
          font-size: 18pt;
          color: #2C5282;
          text-align: center;
          margin-bottom: 8px;
        }
        .header .company {
          font-size: 12pt;
          font-weight: bold;
        }
        .header .date {
          font-size: 10pt;
          color: #666;
        }
        .form-section {
          margin-bottom: 20px;
        }
        .form-section h2 {
          font-size: 13pt;
          color: #2C5282;
          margin-bottom: 12px;
          padding-bottom: 4px;
          border-bottom: 1px solid #ddd;
        }
        .section-description {
          font-size: 10pt;
          color: #666;
          font-style: italic;
          margin-bottom: 12px;
        }
        .form-row {
          margin-bottom: 16px;
        }
        .form-row.inline {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .form-row label {
          display: block;
          font-weight: bold;
          margin-bottom: 4px;
          font-size: 10pt;
        }
        .form-row.inline label {
          margin-bottom: 0;
          white-space: nowrap;
        }
        .input-line {
          border-bottom: 1px solid #333;
          min-height: 24px;
          width: 100%;
        }
        .input-line.short {
          width: 150px;
        }
        .textarea-box {
          border: 1px solid #333;
          min-height: 60px;
          width: 100%;
        }
        .textarea-box.large {
          min-height: 100px;
        }
        .checkbox-group {
          display: flex;
          gap: 24px;
          margin-top: 4px;
        }
        .checklist {
          margin-top: 8px;
        }
        .checklist-item {
          margin-bottom: 8px;
          padding-left: 8px;
        }
        .signature-row {
          display: flex;
          gap: 48px;
          margin-top: 32px;
        }
        .signature-block {
          flex: 1;
        }
        .signature-block.full {
          max-width: 300px;
        }
        .signature-line {
          border-bottom: 1px solid #333;
          min-height: 40px;
          margin-bottom: 4px;
        }
        .signature-block label {
          font-size: 9pt;
          color: #666;
        }
        .terms-text {
          font-size: 10pt;
          line-height: 1.5;
        }
        .terms-text p {
          margin-bottom: 12px;
        }
        .note {
          font-size: 9pt;
          font-style: italic;
          color: #666;
          margin-top: 8px;
        }
        .equipment-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
        }
        .equipment-table th,
        .equipment-table td {
          border: 1px solid #333;
          padding: 8px;
          text-align: left;
        }
        .equipment-table th {
          background: #f5f5f5;
          font-size: 10pt;
        }
        .equipment-table td {
          min-height: 24px;
        }
        ul {
          margin-left: 24px;
          margin-top: 8px;
        }
        ul li {
          margin-bottom: 4px;
        }
        @media print {
          body {
            padding: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${doc.title}</h1>
        <div class="header-top">
          <div class="company">${company.companyName}</div>
          <div class="date">Date: _____________________</div>
        </div>
      </div>
      ${formFields[doc.id] || '<p>Form content not available.</p>'}
    </body>
    </html>
  `;
};

const generateCoverPage = (packageType: 'client' | 'employee' | 'mixed', selectedDocs: DocumentForm[], companyInfo?: CompanyInfo): string => {
  const today = new Date().toLocaleDateString();
  const company = companyInfo || {
    companyName: 'Your Company',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: ''
  };
  const companyAddress = [company.address, company.city, company.state, company.zipCode].filter(Boolean).join(', ');
  
  const packageTitle = packageType === 'client'
    ? 'Client Onboarding Package'
    : packageType === 'employee'
      ? 'Employee Onboarding Package'
      : 'Document Package';

  const packageDescription = packageType === 'client'
    ? `Welcome to ${company.companyName}! This package contains all the forms needed to begin our working relationship. Please complete each form carefully and return them to us before project commencement.`
    : packageType === 'employee'
      ? `Welcome to the ${company.companyName} team! This package contains all the forms required for your employment. Please complete each form carefully and return them to HR on or before your first day.`
      : `This package contains the selected forms from ${company.companyName}. Please complete each form carefully.`;

  const clientDocs = selectedDocs.filter(d => d.category === 'client').sort((a, b) => a.order - b.order);
  const employeeDocs = selectedDocs.filter(d => d.category === 'employee').sort((a, b) => a.order - b.order);

  return `
    <div class="cover-page">
      <div class="cover-header">
        <div class="company-name">${company.companyName}</div>
        <div class="package-title">${packageTitle}</div>
        <div class="date">Prepared: ${today}</div>
      </div>

      <div class="cover-description">
        <p>${packageDescription}</p>
      </div>

      <div class="toc">
        <h2>Contents</h2>
        ${clientDocs.length > 0 ? `
          <div class="toc-section">
            <h3>Client Forms</h3>
            <ol>
              ${clientDocs.map(doc => `<li>${doc.title}</li>`).join('')}
            </ol>
          </div>
        ` : ''}
        ${employeeDocs.length > 0 ? `
          <div class="toc-section">
            <h3>Employee Forms</h3>
            <ol>
              ${employeeDocs.map(doc => `<li>${doc.title}</li>`).join('')}
            </ol>
          </div>
        ` : ''}
      </div>

      <div class="cover-footer">
        <div class="contact-info">
          <p><strong>${company.companyName}</strong></p>
          <p>${companyAddress ? companyAddress : ''}</p>
          <p>${company.phone ? `Phone: ${company.phone}` : ''}${company.phone && company.email ? ' | ' : ''}${company.email ? `Email: ${company.email}` : ''}</p>
        </div>
      </div>
    </div>
  `;
};

const generatePackageHTML = (selectedDocs: DocumentForm[], companyInfo?: CompanyInfo): string => {
  const company = companyInfo || {
    companyName: 'Your Company',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: ''
  };

  // Determine package type
  const hasClient = selectedDocs.some(d => d.category === 'client');
  const hasEmployee = selectedDocs.some(d => d.category === 'employee');
  const packageType = hasClient && hasEmployee ? 'mixed' : hasClient ? 'client' : 'employee';

  // Sort documents: client forms first (by order), then employee forms (by order)
  const sortedDocs = [...selectedDocs].sort((a, b) => {
    if (a.category !== b.category) {
      return a.category === 'client' ? -1 : 1;
    }
    return a.order - b.order;
  });

  const formFields: Record<string, string> = {
    'client-info': `
      <div class="form-section">
        <h2>Client Information</h2>
        <div class="form-row">
          <label>Company/Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Primary Contact:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Email Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Billing Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>City, Province/State, Postal Code:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Site Address (if different):</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Preferred Contact Method:</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Phone</span>
            <span>☐ Email</span>
            <span>☐ Text</span>
          </div>
        </div>
        <div class="form-row">
          <label>How did you hear about us?</label>
          <div class="input-line"></div>
        </div>
      </div>
    `,
    'project-scope': `
      <div class="form-section">
        <h2>Project Information</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Start Date:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Estimated Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Scope of Work</h2>
        <div class="form-row">
          <label>Detailed Description of Work:</label>
          <div class="textarea-box large"></div>
        </div>
        <div class="form-row">
          <label>Materials to be Used:</label>
          <div class="textarea-box"></div>
        </div>
        <div class="form-row">
          <label>Work NOT Included in Scope:</label>
          <div class="textarea-box"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Deliverables</h2>
        <p class="section-description">List specific items, structures, or results that will be completed as part of this project (e.g., "Install 200 sq ft hardwood flooring", "Frame and drywall basement bedroom", "Replace 5 windows with vinyl double-pane"):</p>
        <div class="checklist">
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
          <div class="checklist-item">☐ _________________________________</div>
        </div>
      </div>
    `,
    'service-agreement': `
      <div class="form-section">
        <h2>Parties</h2>
        <div class="form-row">
          <label>Contractor:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Client:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Terms of Service</h2>
        <div class="terms-text">
          <p>1. <strong>Scope of Services:</strong> The Contractor agrees to provide construction and/or renovation services as outlined in the attached Project Scope Agreement.</p>
          <p>2. <strong>Payment Terms:</strong> The Client agrees to pay _____% deposit upon signing, with the balance due upon completion. Payment is due within 30 days of the invoice date. Interest of 2% per month may be charged on overdue accounts.</p>
          <p>3. <strong>Change Orders:</strong> Any changes to the original scope must be documented in writing and signed by both parties before additional work proceeds. Changes may affect the project timeline and total cost.</p>
          <p>4. <strong>Timeline:</strong> Work will commence on __________ with estimated completion by __________. The timeline may be extended due to weather, material availability, permit delays, or Client-requested changes.</p>
          <p>5. <strong>Permits and Inspections:</strong> The Contractor is responsible for obtaining required building permits and scheduling inspections, unless otherwise agreed in writing. Permit fees are ☐ included / ☐ not included in the contract price.</p>
          <p>6. <strong>Warranty:</strong> The Contractor provides a minimum 2-year warranty on workmanship from the date of completion. Manufacturer warranties apply to all materials and fixtures. This warranty does not cover damage caused by misuse, neglect, or normal wear and tear.</p>
          <p>7. <strong>Insurance:</strong> The Contractor maintains commercial general liability insurance and is registered with WorkSafeBC for workers' compensation coverage. Proof of insurance is available upon request.</p>
          <p>8. <strong>Cancellation:</strong> Either party may cancel this agreement with 14 days written notice. Upon cancellation, the Client is responsible for payment of all work completed, materials purchased or ordered, and any restocking fees. Deposits are non-refundable for custom or special-order materials.</p>
          <p>9. <strong>Builders Lien Notice:</strong> Pursuant to the Builders Lien Act of British Columbia, the Contractor and any subcontractors or material suppliers may have lien rights against the property if payment is not received.</p>
          <p>10. <strong>Dispute Resolution:</strong> The parties agree to attempt to resolve any disputes through good-faith negotiation. If unresolved, disputes shall be submitted to mediation before pursuing legal remedies. This agreement is governed by the laws of British Columbia.</p>
          <p>11. <strong>Entire Agreement:</strong> This agreement, together with any attached schedules and the Project Scope Agreement, constitutes the entire agreement between the parties. Any modifications must be in writing and signed by both parties.</p>
        </div>
      </div>
      <div class="form-section">
        <h2>Signatures</h2>
        <p class="section-description">By signing below, both parties acknowledge that they have read, understood, and agree to the terms of this Service Agreement.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'change-order': `
      <div class="form-section">
        <h2>Original Project Information</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Original Contract Date:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Change Order #:</label>
          <div class="input-line short"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Description of Change</h2>
        <div class="form-row">
          <label>Detailed Description:</label>
          <div class="textarea-box large"></div>
        </div>
        <div class="form-row">
          <label>Reason for Change:</label>
          <div class="textarea-box"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Cost Adjustment</h2>
        <div class="form-row inline">
          <label>Additional Materials Cost: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>Additional Labour Cost: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>Total Change Order Amount: $</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row inline">
          <label>New Contract Total: $</label>
          <div class="input-line short"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Timeline Impact</h2>
        <div class="form-row">
          <label>Days Added to Schedule:</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>New Estimated Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Authorization</h2>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature / Date</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature / Date</label>
          </div>
        </div>
      </div>
    `,
    'completion-signoff': `
      <div class="form-section">
        <h2>Project Details</h2>
        <div class="form-row">
          <label>Client Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Project Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Completion Date:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Work Completion Statement</h2>
        <div style="border: 2px solid #333; padding: 16px; margin-bottom: 16px; line-height: 2;">
          <p>I, the undersigned Client, acknowledge and confirm that all work agreed upon for the project:</p>
          <p style="margin-top: 12px;">
            <strong>Project/Job Name:</strong> ________________________________________
          </p>
          <p style="margin-top: 8px;">
            <strong>Project Start Date:</strong> ________________________________________
          </p>
          <p style="margin-top: 12px;">
            has been completed in accordance with the terms of the Service Agreement and Project Scope Agreement. 
            All deliverables specified in the original contract and any approved change orders have been fulfilled to my satisfaction.
          </p>
          <div style="display: flex; justify-content: flex-end; align-items: center; gap: 8px; margin-top: 16px;">
            <span>Client Initials:</span>
            <div style="border: 2px solid #333; width: 60px; height: 40px;"></div>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2 style="display: inline;">Omissions</h2>
        <span class="section-description" style="margin-left: 12px;">List any work from the original scope that was not completed or requires follow-up</span>
        <div style="margin-top: 12px;">
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
          <div class="input-line" style="margin-bottom: 12px;"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Client Acceptance</h2>
        <p>I have inspected the completed work and accept it as satisfactory. All contracted work has been performed to my satisfaction.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Client Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Contractor Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'employee-info': `
      <div class="form-section">
        <h2>Personal Information</h2>
        <div class="form-row">
          <label>Full Legal Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Date of Birth:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label style="display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY, e.g. JohnSmith010190)</span></label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Social Insurance Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Home Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>City, Province, Postal Code:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Email Address:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Emergency Contact</h2>
        <div class="form-row">
          <label>Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Relationship:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Phone Number:</label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Employment Eligibility</h2>
        <div class="form-row">
          <label>Are you legally eligible to work in Canada?</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Yes</span>
            <span>☐ No</span>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2>Certification</h2>
        <p>I certify that the information provided above is true and accurate to the best of my knowledge.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
    'td1-federal': `
      <div class="form-section">
        <h2>TD1 Federal - 2026 Personal Tax Credits Return</h2>
        <p class="note" style="margin-bottom: 16px; font-style: italic; color: #666;">
          This is the official CRA TD1 Federal form. Please print or download the PDF below and complete it as required.
        </p>
        <div style="text-align: center; margin: 24px 0;">
          <a href="/forms/td1-federal-26e.pdf" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #2C5282; color: white; text-decoration: none; border-radius: 8px; font-weight: bold;">
            <span style="font-size: 20px;">📄</span>
            Open TD1 Federal Form (PDF)
          </a>
        </div>
        <div style="border: 2px solid #2C5282; border-radius: 8px; overflow: hidden; margin-top: 16px;">
          <iframe 
            src="/forms/td1-federal-26e.pdf" 
            style="width: 100%; height: 800px; border: none;"
            title="TD1 Federal Form">
          </iframe>
        </div>
        <p style="margin-top: 16px; font-size: 10pt; color: #666; text-align: center;">
          Source: Canada Revenue Agency (CRA) - Form TD1-26E
        </p>
      </div>
    `,
    'td1-provincial': `
      <div class="form-section">
        <h2>TD1BC Provincial - 2026 British Columbia Personal Tax Credits Return</h2>
        <p class="note" style="margin-bottom: 16px; font-style: italic; color: #666;">
          This is the official CRA TD1BC Provincial form for British Columbia. Please print or download the PDF below and complete it as required.
        </p>
        <div style="text-align: center; margin: 24px 0;">
          <a href="/forms/td1bc-provincial-26e.pdf" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #2C5282; color: white; text-decoration: none; border-radius: 8px; font-weight: bold;">
            <span style="font-size: 20px;">📄</span>
            Open TD1BC Provincial Form (PDF)
          </a>
        </div>
        <div style="border: 2px solid #2C5282; border-radius: 8px; overflow: hidden; margin-top: 16px;">
          <iframe 
            src="/forms/td1bc-provincial-26e.pdf" 
            style="width: 100%; height: 800px; border: none;"
            title="TD1BC Provincial Form">
          </iframe>
        </div>
        <p style="margin-top: 16px; font-size: 10pt; color: #666; text-align: center;">
          Source: Canada Revenue Agency (CRA) - Form TD1BC-26E
        </p>
      </div>
    `,
    'direct-deposit': `
      <div class="form-section">
        <h2>Employee Information</h2>
        <div class="form-row">
          <label>Full Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label style="display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY, e.g. JohnSmith010190)</span></label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="form-section">
        <h2>Bank Account Information</h2>
        <div class="form-row">
          <label>Financial Institution Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Branch Address:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row">
          <label>Account Type:</label>
          <div class="checkbox-group" style="flex-wrap: wrap;">
            <span>☐ Chequing</span>
            <span>☐ Savings</span>
          </div>
        </div>
        <div class="form-row">
          <label>Institution Number (3 digits):</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>Transit/Branch Number (5 digits):</label>
          <div class="input-line short"></div>
        </div>
        <div class="form-row">
          <label>Account Number:</label>
          <div class="input-line"></div>
        </div>
        <p class="note" style="font-style: italic; color: #666; margin-top: 12px;">Please attach a void cheque or a direct deposit form from your financial institution confirming the account details above.</p>
      </div>
      <div class="form-section">
        <h2>Deposit Instructions</h2>
        <div class="form-row">
          <label>Deposit Amount:</label>
          <div style="margin-top: 8px;">
            <div style="margin-bottom: 8px;">☐ 100% of net pay</div>
            <div style="margin-bottom: 8px;">☐ Fixed amount: $__________</div>
            <div style="margin-bottom: 8px;">☐ Percentage: __________% of net pay</div>
          </div>
        </div>
      </div>
      <div class="form-section">
        <h2>Authorization</h2>
        <p>I hereby authorize my employer to deposit my pay directly into the bank account specified above. I understand that:</p>
        <ul style="margin-left: 24px; margin-top: 8px; margin-bottom: 12px;">
          <li>It may take 1-2 pay periods for direct deposit to take effect.</li>
          <li>I am responsible for ensuring the accuracy of the account information provided.</li>
          <li>I must notify my employer in writing of any changes to my banking information.</li>
          <li>This authorization remains in effect until I provide written notice of cancellation.</li>
        </ul>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
    `,
        'code-of-conduct': `
      <style>
        .coc-section { margin-bottom: 8px; }
        .coc-section h2 { font-size: 10pt; margin-bottom: 4px; }
        .coc-section p { font-size: 9pt; margin-bottom: 2px; line-height: 1.3; }
        .coc-list { margin-left: 16px; margin-top: 4px; }
        .coc-list p { margin-bottom: 1px; }
      </style>
      <div class="coc-section">
        <div class="form-row" style="margin-bottom: 6px;">
          <label style="font-size: 9pt;">Full Name:</label>
          <div class="input-line"></div>
        </div>
        <div class="form-row" style="margin-bottom: 6px;">
          <label style="font-size: 9pt; display: flex; justify-content: space-between; width: 100%;">Employee ID: <span style="font-weight: normal; font-style: italic; color: #666;">(FirstnameLastname + DOB MMDDYY)</span></label>
          <div class="input-line"></div>
        </div>
      </div>
      <div class="coc-section">
        <h2>A. Professional Conduct</h2>
        <p>As an employee, I agree to:</p>
        <div class="coc-list">
          <p><strong>A1.</strong> Arrive on time and ready to work at the designated start time</p>
          <p><strong>A2.</strong> Notify my supervisor as soon as possible if I will be late or absent</p>
          <p><strong>A3.</strong> Maintain a professional appearance appropriate for construction work</p>
          <p><strong>A4.</strong> Represent the company positively when interacting with clients, suppliers, and the public</p>
          <p><strong>A5.</strong> Follow all instructions from supervisors and site foremen</p>
          <p><strong>A6.</strong> Complete assigned tasks to the best of my ability and to company standards</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>B. Workplace Behaviour</h2>
        <p>I understand that the following behaviours are prohibited and may result in immediate termination:</p>
        <div class="coc-list">
          <p><strong>B1.</strong> Reporting to work under the influence of alcohol or illegal drugs</p>
          <p><strong>B2.</strong> Possession or use of alcohol or illegal drugs on company time or property</p>
          <p><strong>B3.</strong> Theft of company, client, or co-worker property</p>
          <p><strong>B4.</strong> Violence, threats, or intimidation toward any person</p>
          <p><strong>B5.</strong> Harassment or discrimination of any kind</p>
          <p><strong>B6.</strong> Wilful damage to property, tools, or equipment</p>
          <p><strong>B7.</strong> Falsifying timesheets, records, or other company documents</p>
          <p><strong>B8.</strong> Insubordination or refusal to follow reasonable instructions</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>C. Jobsite Expectations</h2>
        <p>While on any jobsite, I agree to:</p>
        <div class="coc-list">
          <p><strong>C1.</strong> Respect the client's property and belongings at all times</p>
          <p><strong>C2.</strong> Keep work areas clean and organized throughout the day</p>
          <p><strong>C3.</strong> Use designated areas for breaks and meals only</p>
          <p><strong>C4.</strong> Not use personal phones except during designated breaks</p>
          <p><strong>C5.</strong> Secure tools and materials at the end of each workday</p>
          <p><strong>C6.</strong> Report any damage, accidents, or incidents immediately to my supervisor</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>D. Tools and Equipment</h2>
        <p>I understand and agree that:</p>
        <div class="coc-list">
          <p><strong>D1.</strong> Company tools and equipment are for work purposes only</p>
          <p><strong>D2.</strong> I am responsible for the care and proper use of tools issued to me</p>
          <p><strong>D3.</strong> Lost, stolen, or damaged tools due to negligence may be deducted from my pay</p>
          <p><strong>D4.</strong> I will not lend company tools to others without authorization</p>
          <p><strong>D5.</strong> I will report any defective or damaged tools immediately</p>
          <p><strong>D6.</strong> All tools must be returned upon termination of employment</p>
        </div>
      </div>
      <div class="coc-section">
        <h2>E. Confidentiality</h2>
        <p>I agree to keep confidential: client personal information and property details, company pricing, estimates, and business information, and co-worker personal information and compensation details.</p>
      </div>
      <div class="coc-section">
        <h2>F. Disciplinary Action</h2>
        <p>I understand that violations may result in: verbal warning, written warning, suspension without pay, or termination of employment. The company reserves the right to determine appropriate action based on severity. Serious violations may result in immediate termination.</p>
      </div>
      <div class="coc-section">
        <h2>Acknowledgment and Agreement</h2>
        <p style="margin-bottom: 8px;">By signing below, I acknowledge that I have read and understand this Code of Conduct Agreement. I agree to abide by these standards and understand that failure to do so may result in disciplinary action up to and including termination.</p>
        <div class="signature-row">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Employee Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Date</label>
          </div>
        </div>
        <div class="signature-row" style="margin-top: 8px;">
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Supervisor/Witness Signature</label>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <label style="font-size: 9pt;">Date</label>
          </div>
        </div>
      </div>
    `,
          };

  const formsHTML = sortedDocs.map(doc => `
    <div class="form-page">
      <div class="header">
        <h1>${doc.title}</h1>
        <div class="header-top">
          <div class="company">${company.companyName}</div>
          <div class="date">Date: _____________________</div>
        </div>
      </div>
      ${formFields[doc.id] || '<p>Form content not available.</p>'}
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Document Package - ${company.companyName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 12pt;
          line-height: 1.4;
        }

        /* Cover Page Styles */
        .cover-page {
          padding: 1in;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          page-break-after: always;
        }
        .cover-header {
          text-align: center;
          padding: 48px 0;
          border-bottom: 3px solid #2C5282;
          margin-bottom: 48px;
        }
        .cover-header .company-name {
          font-size: 28pt;
          font-weight: bold;
          color: #2C5282;
          margin-bottom: 16px;
        }
        .cover-header .package-title {
          font-size: 22pt;
          color: #333;
          margin-bottom: 8px;
        }
        .cover-header .date {
          font-size: 12pt;
          color: #666;
        }
        .cover-description {
          font-size: 12pt;
          line-height: 1.6;
          margin-bottom: 48px;
          text-align: center;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }
        .toc {
          flex: 1;
        }
        .toc h2 {
          font-size: 18pt;
          color: #2C5282;
          margin-bottom: 24px;
          padding-bottom: 8px;
          border-bottom: 2px solid #ddd;
        }
        .toc-section {
          margin-bottom: 24px;
        }
        .toc-section h3 {
          font-size: 14pt;
          color: #333;
          margin-bottom: 12px;
        }
        .toc ol {
          margin-left: 32px;
        }
        .toc li {
          margin-bottom: 8px;
          font-size: 11pt;
        }
        .cover-footer {
          text-align: center;
          padding-top: 32px;
          border-top: 1px solid #ddd;
          margin-top: auto;
        }
        .cover-footer p {
          margin: 4px 0;
          font-size: 10pt;
          color: #666;
        }

        /* Form Page Styles */
        .form-page {
          padding: 0.75in;
          page-break-after: always;
        }
        .form-page:last-child {
          page-break-after: auto;
        }
        .header {
          margin-bottom: 24px;
          padding-bottom: 8px;
          border-bottom: 2px solid #2C5282;
        }
        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .header h1 {
          font-size: 18pt;
          color: #2C5282;
          text-align: center;
          margin-bottom: 8px;
        }
        .header .company {
          font-size: 12pt;
          font-weight: bold;
        }
        .header .date {
          font-size: 10pt;
          color: #666;
        }
        .form-section {
          margin-bottom: 20px;
        }
        .form-section h2 {
          font-size: 13pt;
          color: #2C5282;
          margin-bottom: 12px;
          padding-bottom: 4px;
          border-bottom: 1px solid #ddd;
        }
        .section-description {
          font-size: 10pt;
          color: #666;
          font-style: italic;
          margin-bottom: 12px;
        }
        .form-row {
          margin-bottom: 16px;
        }
        .form-row.inline {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .form-row label {
          display: block;
          font-weight: bold;
          margin-bottom: 4px;
          font-size: 10pt;
        }
        .form-row.inline label {
          margin-bottom: 0;
          white-space: nowrap;
        }
        .input-line {
          border-bottom: 1px solid #333;
          min-height: 24px;
          width: 100%;
        }
        .input-line.short {
          width: 150px;
        }
        .textarea-box {
          border: 1px solid #333;
          min-height: 60px;
          width: 100%;
        }
        .textarea-box.large {
          min-height: 100px;
        }
        .checkbox-group {
          display: flex;
          gap: 24px;
          margin-top: 4px;
        }
        .checklist {
          margin-top: 8px;
        }
        .checklist-item {
          margin-bottom: 8px;
          padding-left: 8px;
        }
        .signature-row {
          display: flex;
          gap: 48px;
          margin-top: 32px;
        }
        .signature-block {
          flex: 1;
        }
        .signature-block.full {
          max-width: 300px;
        }
        .signature-line {
          border-bottom: 1px solid #333;
          min-height: 40px;
          margin-bottom: 4px;
        }
        .signature-block label {
          font-size: 9pt;
          color: #666;
        }
        .terms-text {
          font-size: 10pt;
          line-height: 1.5;
        }
        .terms-text p {
          margin-bottom: 12px;
        }
        .note {
          font-size: 9pt;
          font-style: italic;
          color: #666;
          margin-top: 8px;
        }
        .equipment-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
        }
        .equipment-table th,
        .equipment-table td {
          border: 1px solid #333;
          padding: 8px;
          text-align: left;
        }
        .equipment-table th {
          background: #f5f5f5;
          font-size: 10pt;
        }
        .equipment-table td {
          min-height: 24px;
        }
        ul {
          margin-left: 24px;
          margin-top: 8px;
        }
        ul li {
          margin-bottom: 4px;
        }

        @media print {
          .cover-page, .form-page {
            padding: 0.5in;
          }
        }
      </style>
    </head>
    <body>
      ${generateCoverPage(packageType, selectedDocs, companyInfo)}
      ${formsHTML}
    </body>
    </html>
  `;
};

export const DocumentsPage = () => {
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [selectedDoc, setSelectedDoc] = useState<DocumentForm | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    companyName: 'Your Company',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: ''
  });

  useEffect(() => {
    const loadCompanySettings = async () => {
      try {
        const settings = await getSettings();
        if (settings.company) {
          setCompanyInfo({
            companyName: settings.company.companyName || 'Your Company',
            address: settings.company.address || '',
            city: settings.company.city || '',
            state: settings.company.state || '',
            zipCode: settings.company.zipCode || '',
            phone: settings.company.phone || '',
            email: settings.company.email || '',
            website: settings.company.website || '',
            licenseNumber: settings.company.licenseNumber || ''
          });
        }
      } catch (error) {
        console.error('Failed to load company settings:', error);
      }
    };
    loadCompanySettings();
  }, []);

  const clientDocs = documents.filter(d => d.category === 'client');
  const employeeDocs = documents.filter(d => d.category === 'employee');

  const toggleDoc = (docId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const toggleCategory = (category: 'client' | 'employee') => {
    const categoryDocs = documents.filter(d => d.category === category);
    const allSelected = categoryDocs.every(d => selectedDocs.has(d.id));

    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        categoryDocs.forEach(d => newSet.delete(d.id));
      } else {
        categoryDocs.forEach(d => newSet.add(d.id));
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedDocs(new Set(documents.map(d => d.id)));
  };

  const clearAll = () => {
    setSelectedDocs(new Set());
  };

  const handlePreviewPackage = () => {
    if (selectedDocs.size === 0) return;

    const docs = documents.filter(d => selectedDocs.has(d.id));
    const packageHTML = generatePackageHTML(docs, companyInfo);

    const previewWindow = window.open('', '_blank');
    if (!previewWindow) return;

    previewWindow.document.write(packageHTML);
    previewWindow.document.close();
  };

  const handlePrintPackage = () => {
    if (selectedDocs.size === 0) return;

    const docs = documents.filter(d => selectedDocs.has(d.id));
    const packageHTML = generatePackageHTML(docs, companyInfo);

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(packageHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleDownloadPackage = () => {
    if (selectedDocs.size === 0) return;

    const docs = documents.filter(d => selectedDocs.has(d.id));
    const packageHTML = generatePackageHTML(docs, companyInfo);

    const blob = new Blob([packageHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'onboarding-package.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clientAllSelected = clientDocs.every(d => selectedDocs.has(d.id));
  const clientSomeSelected = clientDocs.some(d => selectedDocs.has(d.id));
  const employeeAllSelected = employeeDocs.every(d => selectedDocs.has(d.id));
  const employeeSomeSelected = employeeDocs.some(d => selectedDocs.has(d.id));

  return (
    <MainLayout
      clientListPanel={
        <div className="flex flex-col h-full bg-white overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Documents</h2>
            <p className="text-gray-500 text-sm">All job files</p>
          </div>
          <div className="p-2">
            <AllDocsFolders />
          </div>
        </div>
      }
    >
      <div className="flex h-full w-full overflow-hidden">
        <div className="flex-1 flex flex-col overflow-y-auto bg-background-light">
          {/* Mobile: job docs live in the main field (left panel is desktop-only) — #70 */}
          <div className="px-2 pt-3 md:hidden">
            <AllDocsFolders />
          </div>
          {/* Forms library — moved into the main field (#70); desktop panel now holds job docs */}
          <div className="md:px-2">
        <div className="flex flex-col h-full bg-white">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Documents</h2>
            <p className="text-gray-500 text-sm">Select documents for package</p>

            {/* BC Building Code 2024 — reference viewer */}
            <a href="/bc-code"
              className="mt-3 flex items-center gap-2.5 rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 px-3 py-2.5 hover:from-primary/15">
              <span className="material-symbols-outlined text-primary text-xl">menu_book</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-gray-900">BC Building Code 2024</span>
                <span className="block text-[11px] text-gray-500">Search the full code — try "shear strap nails"</span>
              </span>
              <span className="material-symbols-outlined text-gray-300 text-lg">chevron_right</span>
            </a>
            
            {/* Selected Documents Info */}
            {selectedDocs.size > 0 && (
              <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {selectedDocs.size} selected
                  </span>
                  <button
                    onClick={clearAll}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePreviewPackage}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">visibility</span>
                    Preview
                  </button>
                  <button
                    onClick={handlePrintPackage}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-bold bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">print</span>
                    Print
                  </button>
                  <button
                    onClick={handleDownloadPackage}
                    className="flex items-center justify-center p-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    title="Download"
                  >
                    <span className="material-symbols-outlined text-sm">download</span>
                  </button>
                </div>
              </div>
            )}
            
            {selectedDocs.size === 0 && (
              <div className="flex gap-2 mt-3">
                <button
                  onClick={selectAll}
                  className="flex-1 px-3 py-2 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
                >
                  Select All
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Client Forms Section */}
            <div className="border-b border-gray-200">
              <div
                className="flex items-center gap-2 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleCategory('client')}
              >
                <input
                  type="checkbox"
                  checked={clientAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = clientSomeSelected && !clientAllSelected;
                  }}
                  onChange={() => toggleCategory('client')}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <span className="material-symbols-outlined text-primary text-lg">groups</span>
                <span className="font-semibold text-gray-900 text-sm">Client Forms</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {clientDocs.filter(d => selectedDocs.has(d.id)).length}/{clientDocs.length}
                </span>
              </div>
              {clientDocs.sort((a, b) => a.order - b.order).map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`flex items-center gap-2 p-3 pl-6 cursor-pointer transition-colors ${
                    selectedDoc?.id === doc.id
                      ? 'bg-primary/10 border-l-4 border-primary'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedDocs.has(doc.id)}
                    onChange={() => toggleDoc(doc.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="material-symbols-outlined text-gray-400 text-base">
                    {doc.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 line-clamp-1">{doc.title}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Employee Forms Section */}
            <div>
              <div
                className="flex items-center gap-2 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleCategory('employee')}
              >
                <input
                  type="checkbox"
                  checked={employeeAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = employeeSomeSelected && !employeeAllSelected;
                  }}
                  onChange={() => toggleCategory('employee')}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                />
                <span className="material-symbols-outlined text-green-600 text-lg">badge</span>
                <span className="font-semibold text-gray-900 text-sm">Employee Forms</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {employeeDocs.filter(d => selectedDocs.has(d.id)).length}/{employeeDocs.length}
                </span>
              </div>
              {employeeDocs.sort((a, b) => a.order - b.order).map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`flex items-center gap-2 p-3 pl-6 cursor-pointer transition-colors ${
                    selectedDoc?.id === doc.id
                      ? 'bg-green-50 border-l-4 border-green-600'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedDocs.has(doc.id)}
                    onChange={() => toggleDoc(doc.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                  />
                  <span className="material-symbols-outlined text-gray-400 text-base">
                    {doc.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 line-clamp-1">{doc.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
          </div>
          {/* Header with actions */}
          <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {selectedDoc 
                  ? selectedDoc.title 
                  : selectedDocs.size > 0
                    ? `Document Package (${selectedDocs.size} page${selectedDocs.size !== 1 ? 's' : ''})`
                    : 'Document Preview'
                }
              </h1>
              {selectedDoc && (
                <p className="text-sm text-gray-500">
                  {selectedDoc.description}
                  {selectedDocs.size > 0 && !selectedDocs.has(selectedDoc.id) && (
                    <span className="ml-2 text-primary font-medium">
                      • {selectedDocs.size} document{selectedDocs.size !== 1 ? 's' : ''} in package
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedDoc ? (
                <>
                  <button
                    onClick={() => handlePreview(selectedDoc, companyInfo)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">open_in_new</span>
                    Open in Tab
                  </button>
                  <button
                    onClick={() => handlePrint(selectedDoc, companyInfo)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">print</span>
                    Print
                  </button>
                </>
              ) : selectedDocs.size > 0 ? (
                <>
                  <button
                    onClick={handlePreviewPackage}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">open_in_new</span>
                    Open in Tab
                  </button>
                  <button
                    onClick={handlePrintPackage}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">print</span>
                    Print Package
                  </button>
                  <button
                    onClick={handleDownloadPackage}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">download</span>
                    Download
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {/* Document Preview Area */}
          <div className="flex-1 p-6 overflow-y-auto">
            {selectedDoc ? (
              // Show single selected document preview
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className={`px-4 py-2 border-b flex items-center justify-between ${
                  selectedDoc.category === 'client' ? 'bg-primary/5 border-primary/20' : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-gray-400 text-base">{selectedDoc.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{selectedDoc.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedDoc.category === 'client' ? 'bg-primary/10 text-primary' : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedDoc.category === 'client' ? 'Client' : 'Employee'}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleDoc(selectedDoc.id)}
                    className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      selectedDocs.has(selectedDoc.id)
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">
                      {selectedDocs.has(selectedDoc.id) ? 'check_circle' : 'add'}
                    </span>
                    {selectedDocs.has(selectedDoc.id) ? 'Added to Package' : 'Add to Package'}
                  </button>
                </div>
                {pdfDocuments[selectedDoc.id] ? (
                  <iframe
                    src={pdfDocuments[selectedDoc.id]}
                    className="w-full border-0"
                    style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}
                    title={selectedDoc.title}
                  />
                ) : (
                  <iframe
                    srcDoc={generateFormHTML(selectedDoc, companyInfo)}
                    sandbox=""
                    className="w-full border-0"
                    style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}
                    title={selectedDoc.title}
                  />
                )}
              </div>
            ) : selectedDocs.size > 0 ? (
              // Show all selected documents as pages (package view)
              <div className="space-y-6">
                {documents
                  .filter(d => selectedDocs.has(d.id))
                  .sort((a, b) => {
                    // Sort by category first (client before employee), then by order
                    if (a.category !== b.category) {
                      return a.category === 'client' ? -1 : 1;
                    }
                    return a.order - b.order;
                  })
                  .map((doc, index) => (
                    <div key={doc.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      {/* Page Header */}
                      <div className={`px-4 py-2 border-b flex items-center justify-between ${
                        doc.category === 'client' ? 'bg-primary/5 border-primary/20' : 'bg-green-50 border-green-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            doc.category === 'client' ? 'bg-primary/20 text-primary' : 'bg-green-200 text-green-700'
                          }`}>
                            Page {index + 1}
                          </span>
                          <span className="material-symbols-outlined text-gray-400 text-base">{doc.icon}</span>
                          <span className="text-sm font-medium text-gray-900">{doc.title}</span>
                        </div>
                        <button
                          onClick={() => toggleDoc(doc.id)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                          title="Remove from package"
                        >
                          <span className="material-symbols-outlined text-lg">close</span>
                        </button>
                      </div>
                      {/* Page Content */}
                      {pdfDocuments[doc.id] ? (
                        <iframe
                          src={pdfDocuments[doc.id]}
                          className="w-full border-0"
                          style={{ height: '800px' }}
                          title={doc.title}
                        />
                      ) : (
                        <iframe
                          srcDoc={generateFormHTML(doc, companyInfo)}
                          sandbox=""
                          className="w-full border-0"
                          style={{ height: '800px' }}
                          title={doc.title}
                        />
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              // Empty state
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">description</span>
                  <p className="text-gray-600 text-lg font-medium">Select a document to preview</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Click a document from the list, or check multiple to build a package
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
